/**
 * MapForge — 격자 맵 에디터.
 *
 * 왼쪽 패널: 타일 팔레트(바닥·벽·프롭) + 도구(칠하기·지우기·스폰) + 회전·층·문서 저장/불러오기 + glb.
 * 무대: 격자 위에 원형을 복제해 찍는다. 클릭/드래그로 칠한다. 스폰 셀엔 캐릭터 작업대의 주인공이 선다.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import * as THREE from 'three';
import { clone as skeletonClone } from 'three/examples/jsm/utils/SkeletonUtils.js';
import { mapAssets, useMapStore, type MapTool } from '../state/map.ts';
import { forgedMeshRef } from '../state/view.ts';
import { buildMapScene, docStats, serializeDoc, KIND_LABEL, type TileKind } from '../core/map/kit.ts';
import { KIT_GALLERY } from '../core/map/kitGallery.ts';
import { downloadBlob, exportSceneGLB } from '../core/export/glb.ts';
import { useStudioEnvironment } from './environment.ts';

// ---------------------------------------------------------------------------
// 무대
// ---------------------------------------------------------------------------

function MapScene() {
  const doc = useMapStore((s) => s.doc);
  const assetsVersion = useMapStore((s) => s.assetsVersion);
  const group = useMemo(() => buildMapScene(doc, mapAssets), [doc, assetsVersion]);
  return <primitive object={group} />;
}

/** 스폰 셀의 주인공 — 캐릭터 작업대에서 만든 리그를 복제해 idle 로 세운다. */
function Hero() {
  const spawn = useMapStore((s) => s.doc.spawn);
  const show = useMapStore((s) => s.showHero);
  const src = forgedMeshRef.current;
  const mixer = useRef<THREE.AnimationMixer | null>(null);
  const obj = useMemo(() => {
    if (!src) return null;
    const c = skeletonClone(src) as THREE.SkinnedMesh;
    c.geometry.computeBoundingBox();
    const bb = c.geometry.boundingBox!;
    const h = Math.max(1e-6, bb.max.y - bb.min.y);
    const s = 1.7 / h;
    const g = new THREE.Group();
    c.scale.setScalar(s);
    c.position.set(-(bb.min.x + bb.max.x) / 2 * s, -bb.min.y * s, -(bb.min.z + bb.max.z) / 2 * s);
    c.frustumCulled = false;
    g.add(c);
    const idle = src.animations.find((a) => a.name === 'idle');
    mixer.current = new THREE.AnimationMixer(c);
    if (idle) mixer.current.clipAction(idle).play();
    return g;
  }, [src]);
  useFrame((_, d) => mixer.current?.update(Math.min(d, 0.1)));
  if (!obj || !spawn || !show) return null;
  return (
    <group position={[spawn.x + 0.5, 0, spawn.z + 0.5]} rotation={[0, -spawn.rot * Math.PI / 2, 0]}>
      <primitive object={obj} />
    </group>
  );
}

function SpawnMarker() {
  const spawn = useMapStore((s) => s.doc.spawn);
  if (!spawn) return null;
  return (
    <mesh position={[spawn.x + 0.5, 0.02, spawn.z + 0.5]} rotation={[-Math.PI / 2, 0, -spawn.rot * Math.PI / 2]}>
      <ringGeometry args={[0.32, 0.42, 24]} />
      <meshBasicMaterial color={0x6ee7ff} transparent opacity={0.9} />
    </mesh>
  );
}

/** 보이지 않는 바닥판 — 포인터 → 셀. 누른 채 움직이면 연속으로 칠한다. */
function PaintPlane() {
  const width = useMapStore((s) => s.doc.width);
  const depth = useMapStore((s) => s.doc.depth);
  const paintAt = useMapStore((s) => s.paintAt);
  const down = useRef(false);
  const last = useRef('');
  const cell = (e: { point: THREE.Vector3 }) => [Math.floor(e.point.x), Math.floor(e.point.z)] as const;
  return (
    <mesh
      position={[width / 2, 0, depth / 2]}
      rotation={[-Math.PI / 2, 0, 0]}
      onPointerDown={(e) => { if (e.button !== 0) return; e.stopPropagation(); down.current = true; const [x, z] = cell(e); last.current = `${x},${z}`; paintAt(x, z); }}
      onPointerUp={() => { down.current = false; }}
      onPointerLeave={() => { down.current = false; hoverRef.current = null; }}
      onPointerMove={(e) => {
        const [x, z] = cell(e);
        hoverRef.current = { x, z };
        if (!down.current) return;
        const k = `${x},${z}`; if (k === last.current) return; last.current = k; paintAt(x, z);
      }}
    >
      <planeGeometry args={[width, depth]} />
      <meshBasicMaterial visible={false} />
    </mesh>
  );
}

const hoverRef: { current: { x: number; z: number } | null } = { current: null };

function HoverCell() {
  const ref = useRef<THREE.Mesh>(null);
  const width = useMapStore((s) => s.doc.width);
  const depth = useMapStore((s) => s.doc.depth);
  const level = useMapStore((s) => s.level);
  const tool = useMapStore((s) => s.tool);
  useFrame(() => {
    const m = ref.current; if (!m) return;
    const h = hoverRef.current;
    const ok = h && h.x >= 0 && h.z >= 0 && h.x < width && h.z < depth;
    m.visible = Boolean(ok);
    if (ok && h) m.position.set(h.x + 0.5, level + 0.015, h.z + 0.5);
  });
  return (
    <mesh ref={ref} rotation={[-Math.PI / 2, 0, 0]}>
      <planeGeometry args={[1, 1]} />
      <meshBasicMaterial color={tool === 'erase' ? 0xff7b7b : tool === 'spawn' ? 0x6ee7ff : 0xffd166} transparent opacity={0.35} depthWrite={false} />
    </mesh>
  );
}

function Lights() {
  const environment = useStudioEnvironment();
  const scene = useThree((s) => s.scene);
  const gl = useThree((s) => s.gl);
  useEffect(() => {
    scene.environment = environment;
    scene.background = new THREE.Color(0x0a0c12);
    gl.toneMapping = THREE.NeutralToneMapping;
    gl.toneMappingExposure = 1.0;
  }, [scene, environment, gl]);
  return (
    <>
      <directionalLight position={[6, 10, 4]} intensity={1.2} castShadow shadow-mapSize={[2048, 2048]} shadow-camera-left={-20} shadow-camera-right={20} shadow-camera-top={20} shadow-camera-bottom={-20} />
      <ambientLight intensity={0.25} />
    </>
  );
}

export const mapSceneRef: { current: THREE.Scene | null } = { current: null };

function SceneGrab() {
  const scene = useThree((s) => s.scene);
  useEffect(() => { mapSceneRef.current = scene; return () => { mapSceneRef.current = null; }; }, [scene]);
  return null;
}

export function MapStage() {
  const width = useMapStore((s) => s.doc.width);
  const depth = useMapStore((s) => s.doc.depth);
  const showGrid = useMapStore((s) => s.showGrid);
  const busy = useMapStore((s) => s.busy);
  const n = Math.max(width, depth);
  return (
    <div className="stage">
      <Canvas shadows dpr={[1, 1.5]} camera={{ position: [width * 0.5 + n * 0.55, n * 0.95, depth * 0.5 + n * 1.05], fov: 40, near: 0.1, far: 500 }} gl={{ antialias: true, preserveDrawingBuffer: true }}>
        <Lights />
        <SceneGrab />
        <OrbitControls target={[width / 2, 0, depth / 2]} makeDefault mouseButtons={{ LEFT: undefined as never, MIDDLE: THREE.MOUSE.PAN, RIGHT: THREE.MOUSE.ROTATE }} />
        {showGrid && <gridHelper args={[n, n, 0x3a445c, 0x232a3a]} position={[n / 2, 0.005, n / 2]} />}
        <PaintPlane />
        <HoverCell />
        <MapScene />
        <SpawnMarker />
        <Hero />
      </Canvas>
      {busy && <div className="overlay"><div className="spinner" /><div className="msg">{busy}</div></div>}
      <div className="stage-tip">좌클릭·드래그 칠하기 · 우클릭 회전 · 휠 줌 · 가운데 버튼 이동</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 패널
// ---------------------------------------------------------------------------

function Palette() {
  const doc = useMapStore((s) => s.doc);
  const selected = useMapStore((s) => s.selected);
  const select = useMapStore((s) => s.select);
  const removeAsset = useMapStore((s) => s.removeAsset);
  const addImages = useMapStore((s) => s.addImages);
  const addFromUrl = useMapStore((s) => s.addFromUrl);
  const [kind, setKind] = useState<TileKind>('prop');
  const input = useRef<HTMLInputElement>(null);
  const kit = KIT_GALLERY.filter((k) => !doc.assets.some((a) => a.name === k.id));

  return (
    <div className="section">
      <h2>타일 팔레트</h2>
      <div className="chips" style={{ marginBottom: 8 }}>
        {(['floor', 'wall', 'prop'] as TileKind[]).map((k) => (
          <button key={k} className="chip" aria-pressed={kind === k} onClick={() => setKind(k)}>{KIND_LABEL[k]}</button>
        ))}
        <button className="chip ghost" onClick={() => input.current?.click()}>+ 이미지 → {KIND_LABEL[kind]}</button>
        <input ref={input} type="file" accept="image/*" multiple hidden onChange={(e) => { const fs = Array.from(e.target.files ?? []); if (fs.length) void addImages(fs, kind); e.target.value = ''; }} />
      </div>
      <p className="hint">바닥·벽은 이미지가 곧 텍스처(정사각 타일). 프롭은 캐릭터와 같은 파이프라인으로 입체가 되어 셀에 맞춰 선다.</p>
      {doc.assets.length > 0 && (
        <div className="tiles">
          {doc.assets.map((a) => (
            <button key={a.id} className="tile" aria-pressed={selected === a.id} onClick={() => select(a.id)} title={a.name}>
              <img src={mapAssets.get(a.id)?.thumb ?? a.image} alt={a.name} />
              <span>{KIND_LABEL[a.kind]} · {a.name}</span>
              <i onClick={(e) => { e.stopPropagation(); removeAsset(a.id); }} title="삭제">×</i>
            </button>
          ))}
        </div>
      )}
      {kit.length > 0 && (
        <>
          <p className="hint" style={{ marginTop: 8 }}>번들 키트 — 눌러서 팔레트에 추가</p>
          <div className="tiles">
            {kit.map((k) => (
              <button key={k.id} className="tile ghost" onClick={() => void addFromUrl(k.url, k.id, k.kind)} title={k.label}>
                <img src={k.url} alt={k.label} />
                <span>{KIND_LABEL[k.kind]} · {k.label}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function Tools() {
  const tool = useMapStore((s) => s.tool);
  const setTool = useMapStore((s) => s.setTool);
  const rot = useMapStore((s) => s.rot);
  const setRot = useMapStore((s) => s.setRot);
  const level = useMapStore((s) => s.level);
  const setLevel = useMapStore((s) => s.setLevel);
  const scale = useMapStore((s) => s.scale);
  const setScale = useMapStore((s) => s.setScale);
  const fill = useMapStore((s) => s.fill);
  const clear = useMapStore((s) => s.clear);
  const doc = useMapStore((s) => s.doc);
  const resize = useMapStore((s) => s.resize);
  const selected = useMapStore((s) => s.selected);
  const selKind = doc.assets.find((a) => a.id === selected)?.kind;
  const tools: { id: MapTool; label: string }[] = [{ id: 'paint', label: '칠하기' }, { id: 'erase', label: '지우기' }, { id: 'spawn', label: '스폰' }];

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.target as HTMLElement)?.tagName === 'INPUT') return;
      if (e.key === 'r' || e.key === 'R') setRot(rot + 1);
      if (e.key === 'e' || e.key === 'E') setTool(tool === 'erase' ? 'paint' : 'erase');
      if (e.key === '[') setLevel(level - 1);
      if (e.key === ']') setLevel(level + 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [rot, tool, level, setRot, setTool, setLevel]);

  return (
    <div className="section">
      <h2>도구</h2>
      <div className="chips">
        {tools.map((t) => <button key={t.id} className="chip" aria-pressed={tool === t.id} onClick={() => setTool(t.id)}>{t.label}</button>)}
        <button className="chip ghost" onClick={() => setRot(rot + 1)}>회전 {rot * 90}° (R)</button>
      </div>
      <label className="field" style={{ marginTop: 8 }}>
        <span className="field-head"><span className="k">층 ([ ])</span><span className="v">{level}</span></span>
        <input type="range" min={0} max={3} step={1} value={level} onChange={(e) => setLevel(Number(e.target.value))} />
      </label>
      <label className="field">
        <span className="field-head"><span className="k">프롭 크기</span><span className="v">{scale.toFixed(2)}×</span></span>
        <input type="range" min={0.3} max={3} step={0.05} value={scale} onChange={(e) => setScale(Number(e.target.value))} />
      </label>
      <div className="btn-row">
        <button className="btn" disabled={selKind !== 'floor'} onClick={fill}>바닥 전체 채우기</button>
        <button className="btn" onClick={clear}>전부 지우기</button>
      </div>
      <div className="btn-row" style={{ marginTop: 6 }}>
        <label className="field"><span className="field-head"><span className="k">가로</span><span className="v">{doc.width}</span></span>
          <input type="range" min={4} max={40} step={1} value={doc.width} onChange={(e) => resize(Number(e.target.value), doc.depth)} /></label>
        <label className="field"><span className="field-head"><span className="k">세로</span><span className="v">{doc.depth}</span></span>
          <input type="range" min={4} max={40} step={1} value={doc.depth} onChange={(e) => resize(doc.width, Number(e.target.value))} /></label>
      </div>
    </div>
  );
}

function DocSection() {
  const doc = useMapStore((s) => s.doc);
  const loadDoc = useMapStore((s) => s.loadDoc);
  const toggle = useMapStore((s) => s.toggle);
  const showGrid = useMapStore((s) => s.showGrid);
  const showHero = useMapStore((s) => s.showHero);
  const [last, setLast] = useState('');
  const [busy, setBusy] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const stats = docStats(doc);
  const hasHero = Boolean(forgedMeshRef.current);

  const saveGlb = async () => {
    setBusy(true);
    try {
      const root = buildMapScene(doc, mapAssets);
      root.updateMatrixWorld(true);
      const blob = await exportSceneGLB(root);
      downloadBlob(blob, `${doc.name || 'map'}.glb`);
      setLast(`${(blob.size / 1024 / 1024).toFixed(2)}MB · 타일 ${doc.placements.length}개`);
    } catch (err) {
      setLast(`실패: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setBusy(false);
    }
  };
  const saveJson = () => downloadBlob(new Blob([serializeDoc(doc)], { type: 'application/json' }), `${doc.name || 'map'}.map.json`);

  return (
    <div className="section">
      <h2>맵 문서</h2>
      <dl className="stat-grid">
        <dt>바닥</dt><dd>{stats.floors}</dd>
        <dt>벽</dt><dd>{stats.walls}</dd>
        <dt>프롭</dt><dd>{stats.props}</dd>
        <dt>스폰</dt><dd>{doc.spawn ? `(${doc.spawn.x}, ${doc.spawn.z})` : '없음'}</dd>
      </dl>
      <div className="chips" style={{ margin: '8px 0' }}>
        <button className="chip" aria-pressed={showGrid} onClick={() => toggle('showGrid')}>격자</button>
        <button className="chip" aria-pressed={showHero} onClick={() => toggle('showHero')} disabled={!hasHero} title={hasHero ? '' : '캐릭터 작업대에서 먼저 캐릭터를 만드세요'}>주인공 표시</button>
      </div>
      <div className="btn-row">
        <button className="btn primary" disabled={busy || doc.placements.length === 0} onClick={() => void saveGlb()}>{busy ? '굽는 중…' : '맵 .glb 저장'}</button>
        <button className="btn" onClick={saveJson}>.map.json 저장</button>
        <button className="btn" onClick={() => input.current?.click()}>.map.json 열기</button>
        <input ref={input} type="file" accept=".json,application/json" hidden onChange={async (e) => { const f = e.target.files?.[0]; if (f) await loadDoc(await f.text()); e.target.value = ''; }} />
      </div>
      {last && <p className="hint">{last}</p>}
      <p className="hint">glb 에는 타일마다 메시가 들어가고 같은 타일은 텍스처를 공유합니다. 스폰 위치는 .map.json 에만 남습니다 — 엔진에서 읽어 캐릭터를 세우세요.</p>
    </div>
  );
}

export function MapPanel() {
  const error = useMapStore((s) => s.error);
  return (
    <aside className="panel left">
      <Palette />
      {error && <div className="error-box">{error}</div>}
      <Tools />
      <DocSection />
    </aside>
  );
}
